import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCourse } from 'services/course/getCourse';
import { useNavigate } from 'react-router';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Divider, Chip, Tabs, Tab } from '@mui/material';
import { IoFitnessOutline } from 'react-icons/io5';
import { MdCategory, MdOutlineDateRange, MdTimeline } from 'react-icons/md';
import { EyeOutlined, PlayCircleOutlined } from '@ant-design/icons';

function Student() {
  const studentId = localStorage.getItem('studentid');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('standalone');

  const { data = {} } = useQuery({
    queryKey: ['courses'],
    queryFn: () => getCourse({ userid: Number(studentId) }),
    cacheTime: 300000,
    refetchOnMount: false
  });

  const courses = data.response || [];
  const filteredCourses = courses.filter((course) => course.sequenceType === activeTab);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={4}>
        🧘 Available Yoga Courses
      </Typography>

      <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} textColor="primary" indicatorColor="primary" sx={{ mb: 3 }}>
        <Tab value="standalone" label="Standalone Courses" />
        <Tab value="continuous" label="Continuous Flow Courses" />
      </Tabs>

      <Grid container spacing={4}>
        {filteredCourses.length === 0 ? (
          <Grid item xs={12} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="h4" fontWeight="bold">
              No {activeTab === 'standalone' ? 'Standalone' : 'Continuous'} Courses Found
            </Typography>
          </Grid>
        ) : (
          filteredCourses.map((course) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.courseId}>
              <Card elevation={6} sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2} gap={1}>
                    <IoFitnessOutline size={22} style={{ color: '#1976d2' }} />
                    <Typography variant="h6" fontWeight={600} textTransform="capitalize">
                      {course.courseName}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MdCategory color="#616161" />
                    <Typography variant="body2">Category: {course.category || 'N/A'}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MdTimeline color="#616161" />
                    <Typography variant="body2">Difficulty: {course.difficulty || 'N/A'}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MdOutlineDateRange color="#616161" />
                    <Typography variant="body2">Created: {new Date(course.createdAt).toLocaleDateString()}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={`${course.asanaCount} Asana${course.asanaCount > 1 ? 's' : ''}`} color="secondary" size="small" />
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, flexDirection: 'column' }}>
                  {course.sequenceType === 'continuous' ? (
                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<PlayCircleOutlined />}
                        onClick={() => navigate(`/asanas/${course.courseId}`, { state: { sequenceType: course.sequenceType } })}
                      >
                        Start Continuous Flow
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<PlayCircleOutlined />}
                      onClick={() =>
                        navigate(`/asanas/${course.courseId}/standalone`, {
                          state: { courseId: course.courseId, sequenceType: course.sequenceType }
                        })
                      }
                    >
                      Practice Asanas
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
}

export default Student;
